/**
 * 30-day daily spend chart.
 *
 * Aggregates the most recent N transactions (excluding grants /
 * refunds) into per-day totals. Renders with @nivo/line in a style
 * consistent with the dashboard charts.
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { useTheme } from '@mui/material/styles';

const dayKey = (iso) => {
  const d = new Date(iso);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const DAYS_BACK = 30;

const SpendChart = ({ transactions = [] }) => {
  const theme = useTheme();

  const data = useMemo(() => {
    // Build a 30-day sliding window keyed by day ISO.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const buckets = {};
    for (let i = DAYS_BACK - 1; i >= 0; i -= 1) {
      const d = new Date(today);
      d.setDate(today.getDate() - i);
      buckets[d.toISOString().slice(0, 10)] = 0;
    }

    transactions.forEach((txn) => {
      // Only count debits (negative amounts) - grants/topups are
      // shown separately in the activity table.
      if (!txn?.ts || !txn?.amount || txn.amount >= 0) return;
      const k = dayKey(txn.ts);
      if (k in buckets) buckets[k] += -txn.amount;  // store as positive
    });

    return [{
      id: 'Spend',
      color: theme.palette.primary.main,
      data: Object.entries(buckets).map(([day, credits]) => ({
        x: day,
        y: Number(credits.toFixed(2)),
      })),
    }];
  }, [transactions, theme]);

  const totalSpend = data[0].data.reduce((s, p) => s + (p.y || 0), 0);

  if (totalSpend === 0) {
    return (
      <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No spend in the last {DAYS_BACK} days
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 220 }}>
      <ResponsiveLine
        data={data}
        margin={{ top: 20, right: 24, bottom: 36, left: 48 }}
        xScale={{ type: 'point' }}
        yScale={{ type: 'linear', min: 0, stacked: false }}
        axisBottom={{
          tickSize: 0,
          tickPadding: 6,
          tickRotation: 0,
          tickValues: data[0].data.filter((_, i) => i % 5 === 0).map((d) => d.x),
          format: (v) => {
            const d = new Date(v);
            return `${d.getMonth() + 1}/${d.getDate()}`;
          },
        }}
        axisLeft={{
          tickSize: 0,
          tickPadding: 8,
          tickValues: 5,
          format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v),
        }}
        enableArea
        areaOpacity={0.12}
        enablePoints={false}
        enableSlices="x"
        curve="monotoneX"
        colors={[theme.palette.primary.main]}
        theme={{
          axis: {
            ticks: { text: { fill: theme.palette.text.secondary, fontSize: 11 } },
          },
          grid: { line: { stroke: theme.palette.divider, strokeWidth: 1 } },
          crosshair: { line: { stroke: theme.palette.text.secondary, strokeWidth: 1, strokeOpacity: 0.5 } },
        }}
        sliceTooltip={({ slice }) => {
          const point = slice.points[0];
          return (
            <Box sx={{
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1, px: 1.5, py: 1,
              boxShadow: theme.shadows[4],
            }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                {new Date(point.data.x).toLocaleDateString(undefined, {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
              </Typography>
              <Typography variant="caption" color="primary.main" sx={{ display: 'block' }}>
                {point.data.y.toLocaleString()} credits spent
              </Typography>
            </Box>
          );
        }}
      />
    </Box>
  );
};

export default SpendChart;
